import { Auth } from 'aws-amplify';
import momentTz from 'moment-timezone';
import moment from 'moment';
import axios from 'axios';

// eslint-disable-next-line max-len
export const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const numberOnlyRegex = /^\+[1-9]\d{0,1}-?\d{3,4}-?\d{5,14}$/;

export const numberRegex = /^[0-9]*$/;
export const nonZeroNumberRegex = /^[1-9][0-9]*$/;
export const decimalRegex = /^\d*\.?\d*$/;
export const onlyNonZeroDecimalRegex = /^(?!0(\.0)?$)(\d)*(\.(5|0))$/;
export const fullDepthOptions = /^(?:Yes|No|yes|no)$/;

export const validateAuth = (success, failure) =>
  Auth.currentSession().then(success).catch(failure);

export const getApiBaseUrl = (userType) =>
  userType === 'internal'
    ? process.env.REACT_APP_AWS_API_URL
    : process.env.REACT_APP_AWS_API_EXTERNAL_URL;

/**
 * Normalises an axios error from the Contact/AAL APIs. `errors[0]` is user-facing
 * text; a 409 or `refresh_required` means the screen is stale and must be reloaded.
 */
export const getApiError = (error, fallback) => {
  const data = error?.response?.data || {};
  const status = error?.response?.status;
  let message = null;
  // 5xx bodies carry raw exception text, never a user-facing message
  if (status >= 500) message = null;
  else if (Array.isArray(data.errors) && data.errors.length > 0) message = data.errors[0];
  else if (typeof data.errors === 'string') message = data.errors;
  else if (typeof data.message === 'string') message = data.message;
  return {
    status,
    message: message || fallback,
    refreshRequired: status === 409 || data.refresh_required === true,
  };
};

// Date-only values (MOJ, inductions) are stored at UTC midnight; read them in UTC so the day never shifts
export const formatUtcDate = (value, format = 'DD/MM/YYYY') =>
  value ? moment.utc(value).format(format) : '';

// UTC ISO timestamps (review, expiry, approvals) shown in the browser's timezone
export const formatLocalTimestamp = (value, format = 'DD/MM/YYYY [at] h:mm A') =>
  value ? moment.utc(value).local().format(format) : '';

export const todayInNz = () => momentTz().tz('Pacific/Auckland').format('YYYY-MM-DD');

// Condition text can arrive with escaped control characters (literal "\n" / "\t") from custDB
export const formatConditionText = (value) =>
  value == null
    ? ''
    : String(value)
        .replace(/\\r\\n|\\n|\\r/g, '\n')
        .replace(/\\t/g, '\t');

export const convertInputTimezone = (momentDateTime, timezone) =>
  momentDateTime ? momentTz.tz(momentDateTime.format('YYYY-MM-DD HH:mm:ss'), timezone) : undefined;

export const convertToLocalTimezone = (momentDateTime, timezone) =>
  momentDateTime ? momentTz.tz(momentDateTime, timezone || moment.tz.guess()) : undefined;

// Helper function to map any string to one of the accent colours
// used in the theme, will always return the same color for the same string.
export const nameToColour = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash += str.charCodeAt(i);
  }
  return `accent${(hash % 5) + 1}`;
};

// This helper function simply gets the initials of a string and capitilizes them.
// For example, the string "Damon Greenhalgh" -> "DG"
export const getInitials = (name) => {
  return name
    .split(' ')
    .map((subname) => subname.charAt(0).toUpperCase())
    .join('');
};

// This helper function returns the time since an event has passed
// from secs, to minutes, to hours, a day -> then date.
export const timeSinceEvent = (time) => {
  const currentTime = new Date();
  const timeOfEvent = new Date(time);
  let diff = currentTime - timeOfEvent; // in milliseconds

  // TODO: simplify this logic
  if (diff < 1000) {
    return 'less than a second ago';
  } else if (diff <= 1000 * 60) {
    return `${Math.floor(diff / 1000)} seconds ago`;
  } else if (diff < 1000 * 60 * 60) {
    return `${Math.floor(diff / (1000 * 60))} minutes ago`;
  } else if (diff < 1000 * 60 * 60 * 24) {
    return `${Math.floor(diff / (1000 * 60 * 60))} hours ago`;
  } else if (diff < 1000 * 60 * 60 * 24 * 2) {
    return `a day ago`;
  } else {
    return `${Math.floor(diff / (1000 * 60 * 60 * 24))} days ago`;
  }
};

//Helper function to split the input by space, capitalize the first letter of each word, decapitalize the rest letters.
// Join the words together into one string and return it.
// For example, the string "good day" -> "Good Day"
export const capitalizeName = (name) => {
  return name
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

//Help function to render text with line breaks.
// It splits the input text by '\n' and returns an array of React elements with <br /> tags in between.
export const renderWithLineBreaks = (text) => {
  if (!text) return null;
  return text.split('\n').map((line, index) => (
    <span key={index}>
      {line}
      <br />
    </span>
  ));
};

// Generic function, to be shared with other components going forward
export const fetchData = async (
  method,
  url,
  params,
  handleData,
  userType = 'external',
  responseType = 'json'
) => {
  return validateAuth(async (session) => {
    try {
      const response = await axios({
        method,
        url: `${
          userType === 'internal'
            ? process.env.REACT_APP_AWS_API_URL
            : process.env.REACT_APP_AWS_API_EXTERNAL_URL
        }${url}`,
        params,
        headers: {
          Authorization: session.getIdToken().getJwtToken(),
        },
        timeout: 15 * 1000,
        responseType,
      });
      console.log('dca response for', url, response);
      // Check if response was ok, then handle data after
      if (response?.status >= 200 && response?.status < 300) {
        handleData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch data from ', url, error);
    }
  });
};

// Helper function to determine if a object is 'Empty', i.e. all values are
// either null, "", [], including nested objects.
// Is a recursive function.
// Created for the use of checking if a filter has been applied
export const isNotEmptyObject = (values) => {
  return Object.values(values).some((val) => {
    // Normal case: check the field to see if its either null, '', or []
    if (val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
      return false;
    }
    // Case: val is its own object {...}
    if (typeof val === 'object' && !Array.isArray(val)) {
      return isNotEmptyObject(val);
    }
    // Passes all checks? -> 'non empty' value within the object
    return true;
  });
};
